/**
 * Legend width computation for the Timezone row legend.
 *
 * ## Layout
 *
 *   ┌──────────────────────┬────────┬──────────────┐
 *   │ #label (flex-shrink) │ spacer │ right col    │
 *   │  #tzname  (.header)  │ 12px   │ 72px fixed   │
 *   │  #offset  (.caption) │        │ #local-time  │
 *   │                      │        │ #local-date  │
 *   └──────────────────────┴────────┴──────────────┘
 *   #content: padding-start:4  padding-end:8
 *   #root: margin-right: 4px
 *   SheetLegend #root: padding-left: 8px
 *
 * ## Constants (from sentinel bounding boxes)
 *
 *   contentPadL   =  4px  (#content padding-inline-start)
 *   contentPadR   =  8px  (#content padding-inline-end)
 *   spacer        = 12px  (#spacer width, flex-shrink:0)
 *   rightCol      = 72px  (std-col width, flex-shrink:0)
 *   rootMarginR   =  4px  (#root margin-right)
 *   SheetLegendPad=  8px  (SheetLegend #root padding-left)
 *                 ──────
 *   OVERHEAD      =108px
 *
 * ## Verified from
 *
 *   sentinel tz="Africa/Abidjan" (tz-legend/layout):
 *     host=177  root=173  content=173
 *     label=77  spacer=12  tzname=77  offset=77
 *     localTime=31  localDate=72
 *     content = 77 + 12 + 72 + 4 + 8 = 173 ✓
 *     host = 173 + rootMarginR(4) = 177 ✓
 *     body = 177 + SheetLegendPad(8) = 185
 *     formula = ceil(77) + 108 = 185 ✓
 *
 * ## Font
 *
 * `#tzname` uses `.header { --font-size: var(--small-size) }` — same
 * font as DefaultRow `.small`. Accessed via `measure.fonts["tzname"]`.
 */

import type { TextMeasurer } from "../../../../sheet/default-legend-layout.ts";

const OVERHEAD = 108;

export interface TimezoneLegendLayoutInput {
  tz: string;
}

/**
 * Compute the natural width of a timezone legend in pixels.
 */
function timezoneLegendWidth(
  input: TimezoneLegendLayoutInput,
  measure: TextMeasurer,
): number {
  const font = measure.fonts["tzname"] ?? null;
  return Math.max(
    150,
    Math.ceil(measure.measureText(input.tz, font)) + OVERHEAD,
  );
}

export namespace TimezoneLegendLayout {
  export const width = timezoneLegendWidth;
}
